import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Trash2, Check, RefreshCw, Clock } from 'lucide-react';
import { message } from 'antd';
import { V, overlayStyle, modalStyle, labelStyle, h2Style, avatarGradient } from '../vividGlass';
import brandDnaActions from '../../../../actions/brandDna';

const TABS = ['Collaborators', 'Pending Invites'];

function Avatar({ name }) {
  return (
    <div style={{
      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
      background: avatarGradient(name || ''),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 800, color: '#fff',
    }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function CollaboratorItem({ collaborator, brandId, isOwner, onRemoved }) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await brandDnaActions.removeCollaborator(brandId, collaborator.client_id);
      message.success('Collaborator removed');
      onRemoved();
    } catch {
      message.error('Failed to remove collaborator');
    } finally {
      setRemoving(false);
      setConfirmRemove(false);
    }
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
      <Avatar name={collaborator.client_name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {collaborator.client_name}
        </div>
        <div style={{ fontSize: 11, color: V.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {collaborator.client_email}
        </div>
      </div>
      {isOwner && (
        confirmRemove ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <motion.button whileTap={{ scale: 0.95 }} onClick={handleRemove} disabled={removing}
              style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,87,108,0.15)', border: '1px solid rgba(245,87,108,0.4)', color: '#f5576c', cursor: 'pointer', fontFamily: 'inherit' }}>
              <Check size={12} />
            </motion.button>
            <motion.button whileTap={{ scale: 0.95 }} onClick={() => setConfirmRemove(false)}
              style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
              <X size={12} />
            </motion.button>
          </div>
        ) : (
          <motion.button whileHover={{ color: '#f5576c' }} whileTap={{ scale: 0.9 }} onClick={() => setConfirmRemove(true)}
            title="Remove collaborator"
            style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: V.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
            <Trash2 size={13} />
          </motion.button>
        )
      )}
    </motion.div>
  );
}

function InviteItem({ invite, brandId, onChanged }) {
  const [acting, setActing] = useState(null);

  const handleCancel = async () => {
    setActing('cancel');
    try {
      await brandDnaActions.cancelBrandInvitation(brandId, invite.invitation_id);
      message.success('Invitation cancelled');
      onChanged();
    } catch {
      message.error('Failed to cancel');
    } finally {
      setActing(null);
    }
  };

  const handleResend = async () => {
    setActing('resend');
    try {
      await brandDnaActions.resendBrandInvitation(brandId, invite.invitation_id);
      message.success('Invitation resent');
    } catch {
      message.error('Failed to resend');
    } finally {
      setActing(null);
    }
  };

  const statusColor = invite.status === 'pending' ? V.warning : invite.status === 'accepted' ? '#4ade80' : '#f5576c';

  return (
    <motion.div layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: `1px solid ${V.glassBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Clock size={14} color={V.textMuted} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: V.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {invite.invitee_email}
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: statusColor }}>
          {invite.status}
        </span>
      </div>
      {invite.status === 'pending' && (
        <div style={{ display: 'flex', gap: 4 }}>
          <motion.button whileTap={{ scale: 0.95 }} onClick={handleResend} disabled={acting === 'resend'}
            title="Resend invitation"
            style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, cursor: 'pointer', fontFamily: 'inherit' }}>
            <RefreshCw size={12} />
          </motion.button>
          <motion.button whileTap={{ scale: 0.95 }} onClick={handleCancel} disabled={acting === 'cancel'}
            title="Cancel invitation"
            style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,87,108,0.08)', border: '1px solid rgba(245,87,108,0.25)', color: '#f5576c', cursor: 'pointer', fontFamily: 'inherit' }}>
            <X size={12} />
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}

export default function CollaboratorsModal({ brandId, collaborators, isOwner, open, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const fetchInvitations = useCallback(async () => {
    if (!isOwner) return;
    setLoadingInvites(true);
    try {
      const res = await brandDnaActions.listBrandInvitations(brandId);
      if (res.success) setInvitations(res.data || []);
    } catch {
      setInvitations([]);
    } finally {
      setLoadingInvites(false);
    }
  }, [brandId, isOwner]);

  useEffect(() => {
    if (open && isOwner) fetchInvitations();
  }, [open, fetchInvitations, isOwner]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      message.warning('Enter an email address');
      return;
    }
    setSending(true);
    try {
      await brandDnaActions.sendBrandInvitation(brandId, inviteEmail.trim());
      message.success('Invitation sent');
      setInviteEmail('');
      fetchInvitations();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={overlayStyle} onClick={onClose}>
          <motion.div
            initial={{ scale: 0.93, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 24 }}
            onClick={(e) => e.stopPropagation()}
            style={{ ...modalStyle, maxWidth: 480 }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ ...h2Style }}>Collaborators</h2>
              <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
                style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                <X size={14} />
              </motion.button>
            </div>

            {/* Tabs */}
            {isOwner && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, padding: 4, borderRadius: 12, background: 'rgba(255,255,255,0.04)' }}>
                {TABS.map((tab, i) => (
                  <motion.button key={tab} onClick={() => setActiveTab(i)} whileTap={{ scale: 0.97 }}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 9, border: 'none', fontFamily: V.font, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                      background: activeTab === i ? 'rgba(79,58,125,0.4)' : 'transparent',
                      color: activeTab === i ? '#C4B5FD' : V.textMuted,
                    }}>
                    {tab}
                    {i === 0 && collaborators.length > 0 && (
                      <span style={{ marginLeft: 5, padding: '1px 6px', borderRadius: 99, background: 'rgba(79,58,125,0.3)', fontSize: 10, color: '#C4B5FD' }}>
                        {collaborators.length}
                      </span>
                    )}
                  </motion.button>
                ))}
              </div>
            )}

            {/* Tab: Collaborators */}
            {activeTab === 0 && (
              <>
                {collaborators.length === 0 ? (
                  <p style={{ color: V.textSub, fontSize: 14, textAlign: 'center', padding: '20px 0', fontFamily: V.font }}>
                    No collaborators yet
                  </p>
                ) : (
                  <AnimatePresence>
                    {collaborators.map((c) => (
                      <CollaboratorItem key={c.collaborator_id} collaborator={c} brandId={brandId} isOwner={isOwner} onRemoved={onRefresh} />
                    ))}
                  </AnimatePresence>
                )}

                {/* Invite input */}
                {isOwner && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${V.glassBorder}` }}>
                    <label style={{ ...labelStyle, marginBottom: 8 }}>Invite by Email</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleInvite(); }}
                        placeholder="collaborator@example.com"
                        type="email"
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`, color: V.text, fontSize: 14, outline: 'none', fontFamily: V.font }}
                      />
                      <motion.button whileTap={{ scale: 0.96 }} onClick={handleInvite} disabled={sending}
                        style={{ padding: '10px 16px', borderRadius: 12, background: 'linear-gradient(90deg,#FF7E67,#FF4D8D)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: V.font, opacity: sending ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <UserPlus size={14} />
                        {sending ? 'Sending…' : 'Invite'}
                      </motion.button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Tab: Pending Invites */}
            {activeTab === 1 && isOwner && (
              <>
                {loadingInvites ? (
                  <p style={{ color: V.textSub, fontSize: 14, textAlign: 'center', padding: '20px 0', fontFamily: V.font }}>Loading…</p>
                ) : invitations.length === 0 ? (
                  <p style={{ color: V.textSub, fontSize: 14, textAlign: 'center', padding: '20px 0', fontFamily: V.font }}>No pending invitations</p>
                ) : (
                  <AnimatePresence>
                    {invitations.map((inv) => (
                      <InviteItem key={inv.invitation_id} invite={inv} brandId={brandId} onChanged={() => { fetchInvitations(); onRefresh(); }} />
                    ))}
                  </AnimatePresence>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
